/**
 * ROC Curve Comparison Component
 * 
 * Displays overlay ROC curves for multiple models.
 * Uses Chart.js for visualization.
 */

import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box
} from '@mui/material';
import { ShowChart } from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface ROCCurveComparisonProps {
  data: {
    models: Array<{
      model_id: string;
      algorithm: string;
      version: number;
      roc_curve?: {
        fpr: number[];
        tpr: number[];
        thresholds: number[];
        auc: number;
      };
    }>;
  };
}

const COLORS = [
  '#3b82f6', // Blue
  '#10b981', // Green
  '#f59e0b', // Amber
  '#ef4444', // Red
];

export const ROCCurveComparison: React.FC<ROCCurveComparisonProps> = ({ data }) => {
  const modelsWithROC = data.models.filter(m => m.roc_curve);

  if (modelsWithROC.length === 0) {
    return null;
  }

  const chartData = {
    datasets: [
      // Diagonal reference line (random classifier)
      {
        label: 'Random Classifier',
        data: [
          { x: 0, y: 0 },
          { x: 1, y: 1 }
        ],
        borderColor: '#6b7280',
        borderDash: [5, 5],
        borderWidth: 2,
        pointRadius: 0,
        fill: false
      },
      // Model ROC curves
      ...modelsWithROC.map((model, idx) => ({
        label: `${model.algorithm.replace(/_/g, ' ')} v${model.version} (AUC: ${model.roc_curve!.auc.toFixed(3)})`,
        data: model.roc_curve!.fpr.map((fpr, i) => ({
          x: fpr,
          y: model.roc_curve!.tpr[i]
        })),
        borderColor: COLORS[idx % COLORS.length],
        backgroundColor: 'transparent',
        borderWidth: 3,
        pointRadius: 0,
        fill: false,
        tension: 0.1
      }))
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          color: '#9ca3af',
          font: {
            size: 12
          },
          padding: 15,
          usePointStyle: true
        }
      },
      title: {
        display: false
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: function(context: any) {
            const label = context.dataset.label || '';
            const x = context.parsed.x.toFixed(3);
            const y = context.parsed.y.toFixed(3);
            return `${label}: FPR=${x}, TPR=${y}`;
          }
        }
      }
    },
    scales: {
      x: {
        type: 'linear' as const,
        title: {
          display: true,
          text: 'False Positive Rate',
          color: '#9ca3af',
          font: {
            size: 14,
            weight: 'bold' as const
          }
        },
        min: 0,
        max: 1,
        ticks: {
          color: '#9ca3af',
          stepSize: 0.2
        },
        grid: {
          color: '#374151'
        }
      },
      y: {
        type: 'linear' as const,
        title: {
          display: true,
          text: 'True Positive Rate',
          color: '#9ca3af',
          font: {
            size: 14,
            weight: 'bold' as const
          }
        },
        min: 0,
        max: 1,
        ticks: {
          color: '#9ca3af',
          stepSize: 0.2
        },
        grid: {
          color: '#374151'
        }
      }
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false
    }
  };

  return (
    <Card sx={{ bgcolor: 'grey.900', border: '1px solid', borderColor: 'grey.800' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShowChart />
          ROC Curve Comparison
        </Typography>
        
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          Receiver Operating Characteristic curves showing the trade-off between true positive rate and false positive rate.
          Higher AUC (Area Under Curve) indicates better model performance.
        </Typography>

        <Box sx={{ height: 400, position: 'relative' }}>
          <Line data={chartData} options={options} />
        </Box>

        {/* AUC Summary */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.800', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ color: 'white', mb: 1 }}>
            AUC Scores
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {modelsWithROC.map((model, idx) => (
              <Box key={model.model_id}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {model.algorithm.replace(/_/g, ' ')} v{model.version}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: COLORS[idx % COLORS.length],
                    fontWeight: 600 
                  }}
                >
                  {model.roc_curve!.auc.toFixed(4)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ROCCurveComparison;
